import React from 'react';
import {FormattedMessage} from 'react-intl';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';

import './ProductVariants.sass';

import throttle from 'lodash-es/throttle';

import SliderGoldPicker from './goldPickers/SliderGoldPicker';

import ProductOptionsPicker, {applyOptions, calculateOption} from '../productPurchase/ProductOptionsPicker';
import Pricing from '../pricing/Pricing';
import FieldSelect from '../fields/fieldSelect/FieldSelect';
import Tooltip from '../tooltip/Tooltip';

import ChannelPrice from "../../containers/channelUtils/ChannelPrice";

const optionsWidgetTypeMap = [
    'season-of-discovery-gold',
];

@connect(({products, channels}) => (
    {
      channels: channels,
      products: products
    }
))
export default class GoldCalculatorVariant extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isSelectWidgetCsr: false,
    };
  }
  static propTypes = {
    price: PropTypes.number.isRequired,
    calculator: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    quantitySuggestions: PropTypes.array,
    options: PropTypes.array
  };

  static defaultProps = {
    quantitySuggestions: [],
    options: []
  };

  static isSelectWidget = product => optionsWidgetTypeMap.includes(product);

  static describeProductChoice = product =>
    `${product.variant.goldCalculator.calculatorUnitLabel} - ${product.quantity}`;

  static findDiscount = (discounts, byField, fieldValue) => discounts
    .sort((a, b) => (b.discount - a.discount))
    .find(discount => fieldValue >= discount[byField]) || {discount: 0};

  static quantityToPrice = (quantity, basePrice, discounts, appliedOptions) => {
    const discount = GoldCalculatorVariant.findDiscount(discounts, 'goldAmount', quantity);
    const discountDelta = basePrice * quantity * discount.discount;

    return applyOptions(basePrice, quantity, appliedOptions) - discountDelta;
  };

  static priceToQuantity = (price, basePrice, discounts, options) => {
    const totalPrice = options
      .filter(it => it.pricing.operation === OPTION_OPERATION_TOTAL_MULTIPLY)
      .reduce((acc, it) => totalPrice / (1 + it.pricing.modifier), price);

    const quantityIndependentOptionsCost = options
      .filter(it => !!it.quantityIndependent && it.pricing.operation !== OPTION_OPERATION_TOTAL_MULTIPLY)
      .map(it => calculateOption(basePrice, 1, it))
      .reduce((acc, it) => acc + it, 0);

    const quantityDependentOptionsCost = options
      .filter(it => !it.quantityIndependent && it.pricing.operation !== OPTION_OPERATION_TOTAL_MULTIPLY)
      .map(it => calculateOption(basePrice, 1, it))
      .reduce((acc, it) => acc + it, 0);

    const discount = discounts.find(
      discount => GoldCalculatorVariant.quantityToPrice(discount.goldAmount, basePrice, [discount], options) <= price
    ) || {discount: 0};

    return (totalPrice - quantityIndependentOptionsCost) / (basePrice * (1 - discount.discount) + quantityDependentOptionsCost);
  };

  static _stateFromProps = ({price, options, calculator, quantitySuggestions, products}) => {

    const discountThresholds = calculator.discountThresholds.sort((a, b) => b.discount - a.discount);

    const minTotalCostQuantity = Math.ceil((calculator.minTotalCost || 0) / price);
    const minQuantity = Math.max(minTotalCostQuantity, calculator.minimumQuantity);
    const quantity = Math.max(minQuantity, quantitySuggestions.length ? Number(quantitySuggestions[0]) : 1);

    const currentDiscount = GoldCalculatorVariant.findDiscount(discountThresholds, 'goldAmount', quantity);
    const currentDiscountIdx = discountThresholds.findIndex(it => it === currentDiscount) || 0;
    const nextDiscount = currentDiscountIdx > 0 ? discountThresholds[currentDiscountIdx - 1] : {discount: 0};
    const firstFilteredOption = options.filter((item) => item.pricing.operation !== 'disabled')[0];
    const isSelectWidget = GoldCalculatorVariant.isSelectWidget(...Object.keys(products))

    return {
      discountThresholds,
      minQuantity,
      quantity,
      selectedOptions: isSelectWidget ? [firstFilteredOption.id] : [],
      calculatedPrice: GoldCalculatorVariant.quantityToPrice(quantity, price, discountThresholds, isSelectWidget ? [firstFilteredOption] : []),
      calculatedOriginalPrice: GoldCalculatorVariant.quantityToPrice(quantity, price, [], isSelectWidget ? [firstFilteredOption] : []),
      currentDiscount,
      nextDiscount
    };
  };

  onChange(newState) {
    this.props.onCostChange(newState.calculatedPrice, newState.calculatedOriginalPrice)
    newState = {...newState, error: newState.quantity < newState.minQuantity};
    this.setState(newState);
    this.props.onChange({
      quantity: newState.quantity,
      options: newState.selectedOptions,
      error: newState.error
    });
  }

  componentWillMount() {
    this.onChange(GoldCalculatorVariant._stateFromProps(this.props));
  }

  componentWillReceiveProps(nextProps) {
    const {price, calculator, options} = nextProps;
    if (price !== this.props.price || calculator !== this.props.calculator || options !== this.props.options) {
      this.onChange(GoldCalculatorVariant._stateFromProps(nextProps));
    }
  }

  componentDidMount() {
    const isSelectWidgetClient = () => {
      if (GoldCalculatorVariant.isSelectWidget(window.location.pathname.replace('/service/',''))) {
        this.setState({isSelectWidgetCsr: true})
      }
    }
    isSelectWidgetClient();
  }

  onPriceUpdate(newPrice, selectedOptions) {
    newPrice = parseFloat(newPrice * (10 ** 2)).toFixed(2);
    if (Number.isNaN(newPrice) || newPrice < 0) {
      newPrice = 0;
    }

    const {options, price} = this.props;
    const {discountThresholds} = this.state;

    const appliedOptions = options.filter(it => selectedOptions.includes(it.id));

    const quantity = GoldCalculatorVariant.priceToQuantity(
      newPrice,
      price,
      discountThresholds,
      appliedOptions
    );

    const currentDiscount = GoldCalculatorVariant.findDiscount(discountThresholds, 'goldAmount', quantity);
    const currentDiscountIdx = discountThresholds.findIndex(it => it === currentDiscount) || 0;
    const nextDiscount = currentDiscountIdx > 0 ? discountThresholds[currentDiscountIdx - 1] : {discount: 0};

    this.onChange({
      ...this.state,
      selectedOptions,
      calculatedPrice: newPrice,
      calculatedOriginalPrice: GoldCalculatorVariant.quantityToPrice(
        quantity,
        price,
        [],
        appliedOptions
      ),
      quantity,
      currentDiscount,
      nextDiscount
    });
  }

  onQuantityUpdate(newQuantity, selectedOptions) {
    newQuantity = parseFloat(newQuantity);
    if (Number.isNaN(newQuantity) || newQuantity < 0) {
      newQuantity = 0;
    }

    const {options, price} = this.props;
    const {discountThresholds} = this.state;

    const currentDiscount = GoldCalculatorVariant.findDiscount(discountThresholds, 'goldAmount', newQuantity);
    const currentDiscountIdx = discountThresholds.findIndex(it => it === currentDiscount) || 0;
    const nextDiscount = currentDiscountIdx > 0 ? discountThresholds[currentDiscountIdx - 1] : {discount: 0};

    const appliedOptions = options.filter(it => selectedOptions.includes(it.id));

    this.onChange({
      ...this.state,
      selectedOptions,
      calculatedPrice: GoldCalculatorVariant.quantityToPrice(
        newQuantity,
        price,
        discountThresholds,
        appliedOptions
      ),
      calculatedOriginalPrice: GoldCalculatorVariant.quantityToPrice(
        newQuantity,
        price,
        [],
        appliedOptions
      ),
      quantity: newQuantity,
      currentDiscount,
      nextDiscount
    });
  }

  throttledOnQuantityUpdate = throttle(this.onQuantityUpdate, 50);

  onOptionsUpdate(options) {
    const {quantity} = this.state;
    this.onQuantityUpdate(quantity, options);
  }

  render() {
    const {price, calculator, options, products, channels} = this.props;
    const {
      selectedOptions,
      quantity, minQuantity,
      calculatedPrice, calculatedOriginalPrice,
      currentDiscount, nextDiscount, isSelectWidgetCsr,
    } = this.state;

    const pickLastProduct = (items) => {
      const keys = Object.keys(items);
      const lastKey = keys[keys.length - 1];
      return GoldCalculatorVariant.isSelectWidget(lastKey)
    }

    const renderSelectOrCheckbox = () => {
      if (products && (pickLastProduct(products) || isSelectWidgetCsr)) {
        return (
        <FieldSelect onChange={evt => this.onOptionsUpdate([Number(evt.target.value)])}>
          {options
              .filter((item) => item.pricing.operation !== 'disabled')
              .map(item => ({...item, priceDelta: calculateOption(price, quantity, item)}))
              .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} {item.pricing.price !== 0 ?
                      `${item.priceDelta > 0 ? '+' : '-'}
                       ${channels.availableChannels[channels.activeChannel].baseCurrency.symbol}${ChannelPrice.formatPrice(channels.availableChannels[channels.activeChannel].baseCurrency, Math.abs(item.priceDelta))}` : null}
                  </option>
              ))}
        </FieldSelect>
        )
      } else {
        return (
            <ProductOptionsPicker
                basePrice={price}
                quantity={quantity}
                availableOptions={options}
                selectedOptionIds={selectedOptions}
                onChange={options => this.onOptionsUpdate(options)}
        />
        )
      }
    }

    return (
      <div className="product-variant product-variant--gold">

        {options.length > 0 && renderSelectOrCheckbox()}

        <SliderGoldPicker
          isShowCustomInput={products && (pickLastProduct(products) || isSelectWidgetCsr)}
          min={minQuantity}
          max={calculator.maximumQuantity}
          unitLabel={calculator.calculatorUnitLabel}
          value={quantity}
          onChange={quantity => this.throttledOnQuantityUpdate(quantity, selectedOptions)}
        />

        {!!nextDiscount.discount && (
          <div className="product__discount">
            <FormattedMessage id="page.product.discount.regular.prefix"/>
            {` ${nextDiscount.goldAmount} ${calculator.calculatorUnitLabel} `}
            <FormattedMessage id="page.product.discount.regular.middle"/>
            {` ${nextDiscount.discount * 100}% `}
            <FormattedMessage id="page.product.discount.off"/>
          </div>
        )}

        {(!!currentDiscount.discount && !nextDiscount.discount) && (
          <div className="product__discount product__discount--max">
            <FormattedMessage id="page.product.discount.max.prefix"/>
            {` ${currentDiscount.discount * 100}% `}
            <FormattedMessage id="page.product.discount.off"/>
          </div>
        )}

        <div className="product__price">
          <Pricing
            accented
            value={calculatedPrice}
            originalValue={calculatedPrice !== calculatedOriginalPrice ? calculatedOriginalPrice : undefined}
          />

          <div className="product__bonus">
            <FormattedMessage
              id="page.checkout.cashback.total"
              values={{amount: <ChannelPrice component="b" price={calculatedPrice * 0.03}/>}}
            />

            <Tooltip
              content={<FormattedMessage id="page.checkout.cashback.tooltip.full"/>}
              open={this.state.openTooltip}
            >
              <div className='tooltip_trigger' onMouseLeave={() => this.setState({openTooltip: false})} onMouseEnter={() => this.setState({openTooltip: true})} />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./components/productVariants/GoldCalculatorVariant.js